import React from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { ChevronRight, CheckCircle2, Phone, ArrowRight, MapPin } from "lucide-react";
import { getServiceArea, SERVICE_AREAS, SEO_SERVICES, SITE } from "../seo";
import { COMPANY, ASSETS, TESTIMONIALS, TRUST_BADGES } from "../mock";
import { SectionHeading, TestimonialCard } from "../components/common";
import { Button } from "../components/ui/button";
import useScrollReveal from "../hooks/useScrollReveal";
import useSEO from "../hooks/useSEO";

export default function ServiceArea() {
  const { city } = useParams();
  const area = getServiceArea(city);
  const navigate = useNavigate();
  useScrollReveal([city]);
  useSEO({
    title: area ? `Screen & Pool Enclosures in ${area.full} | Miller & Miller Services` : "Service Areas | Miller & Miller",
    description: area
      ? `Custom pool cages, screen enclosures, patio screening & rescreening in ${area.full}. Licensed, family-owned & local. Serving ${area.zips.join(", ")}. Free estimates - 321-608-0255.`
      : "",
    keywords: area
      ? [`pool screen enclosure ${area.name} FL`, `pool cage ${area.name}`, `screen enclosure ${area.name} FL`, `patio screening ${area.name}`, `rescreening ${area.name} FL`]
      : [],
    path: area ? `/service-areas/${area.slug}` : "/service-areas",
    jsonLd: area
      ? {
          "@context": "https://schema.org",
          "@type": "GeneralContractor",
          name: `${SITE.name} - ${area.full}`,
          telephone: "+13216080255",
          areaServed: { "@type": "City", name: area.name },
          address: { "@type": "PostalAddress", addressLocality: area.name, addressRegion: "FL", postalCode: area.zips[0], addressCountry: "US" },
        }
      : undefined,
  });

  if (!area) return <Navigate to="/service-areas" replace />;

  const nearbySlugs = SERVICE_AREAS.filter((c) => area.nearby.includes(c.name));

  return (
    <div>
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-24 bg-brand-navy text-white overflow-hidden">
        <img src={ASSETS.pool2} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navydark/92 via-brand-navy/85 to-brand-ocean/50" />
        <div className="container-x relative">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-white/60 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/service-areas" className="hover:text-white">Service Areas</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-brand-gold2">{area.name}</span>
          </nav>
          <span className="inline-flex items-center gap-2 text-xs font-heading font-bold uppercase tracking-wider bg-white/10 rounded-full px-3 py-1.5 ring-1 ring-white/20">
            <MapPin className="h-3.5 w-3.5 text-brand-gold2" /> {area.zips.join(" · ")}
          </span>
          <h1 className="mt-4 text-3xl md:text-5xl font-heading font-extrabold max-w-3xl">Screen & Pool Enclosures in {area.full}</h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">{area.blurb}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button onClick={() => navigate("/estimate")} className="h-12 px-7 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-bold shadow-gold">
              Get My Free Quote <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white/10 hover:bg-white/20 text-white font-heading font-bold ring-1 ring-white/25 transition-colors">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Services in this city */}
      <section className="section-y bg-white">
        <div className="container-x">
          <SectionHeading eyebrow={`Services in ${area.name}`} title={`What We Build in ${area.name}, FL`} subtitle={`From new pool cages to fast rescreening, ${area.name} homeowners trust our licensed, family-owned team for durable, code-compliant outdoor structures.`} />
          <div className="mt-8 md:mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {SEO_SERVICES.map((s, i) => (
              <Link key={s.slug} to={s.path} className="reveal group overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-brand-mist hover:ring-brand-sky/40 transition-all" style={{ transitionDelay: `${i * 50}ms` }}>
                <div className="h-32 overflow-hidden">
                  <img src={s.image} alt={s.nav} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-4">
                  <h3 className="font-heading font-extrabold text-brand-navy text-sm group-hover:text-brand-ocean transition-colors">{s.nav}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why local */}
      <section className="section-y bg-brand-mist/40">
        <div className="container-x grid lg:grid-cols-2 gap-10 items-center">
          <div className="reveal">
            <SectionHeading eyebrow="Local & Trusted" title={`Your ${area.name} Screen Enclosure Experts`} />
            <p className="mt-4 text-brand-slate leading-relaxed">We live and work on the Space Coast, so we understand {area.name}'s coastal climate, local building codes, and HOA requirements. Every enclosure is custom-engineered to Florida Building Code wind loads and built with corrosion-resistant materials made to last.</p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {TRUST_BADGES.map((b) => (
                <li key={b} className="flex items-center gap-2.5 text-sm font-semibold text-brand-navy">
                  <CheckCircle2 className="h-5 w-5 text-brand-ocean shrink-0" /> {b}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-brand-slate"><span className="font-heading font-bold text-brand-navy">ZIP codes served:</span> {area.zips.join(", ")}</p>
          </div>
          <div className="reveal rounded-3xl overflow-hidden shadow-card">
            <img src={ASSETS.pool1} alt={`Pool enclosure project near ${area.name}, FL`} className="w-full h-[380px] object-cover" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-y bg-white">
        <div className="container-x">
          <SectionHeading center eyebrow="Reviews" title="What Space Coast Homeowners Say" />
          <div className="mt-8 md:mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {TESTIMONIALS.map((t, i) => <TestimonialCard key={i} t={t} />)}
          </div>
        </div>
      </section>

      {/* Nearby areas */}
      {nearbySlugs.length > 0 && (
        <section className="section-y bg-brand-mist/40">
          <div className="container-x">
            <SectionHeading center eyebrow="Nearby" title="We Also Serve" />
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {nearbySlugs.map((c) => (
                <Link key={c.slug} to={`/service-areas/${c.slug}`} className="reveal inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white ring-1 ring-brand-mist hover:ring-brand-ocean text-brand-navy font-heading font-bold text-sm transition-all">
                  <MapPin className="h-4 w-4 text-brand-ocean" /> {c.name}, FL
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-gradient-to-r from-brand-ocean to-brand-sky">
        <div className="container-x py-14 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-white">Free estimates in {area.name}, FL</h2>
          <p className="text-white/90 mt-3">Fast, friendly, no-obligation - let's design your perfect outdoor space.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button onClick={() => navigate("/estimate")} className="h-12 px-8 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-extrabold shadow-gold">
              Get My Free Quote <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-brand-navy/30 hover:bg-brand-navy/50 text-white font-heading font-bold ring-1 ring-white/30 transition-colors">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
